import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TabDef<T extends string = string> {
  key: T;
  label: string;
  /** Rendered after the label — a count, usually. */
  badge?: React.ReactNode;
}

/**
 * The underlined tab strip the detail frames draw.
 *
 * `Catalog.tsx` and `Settings.tsx` each hand-roll one of these; this is the
 * third, written once with the roles and the keyboard behaviour a hand-rolled
 * strip of `<button>`s does not have. Arrow keys move between tabs, Home and
 * End jump to the ends, and only the selected tab is in the Tab order — which
 * is what makes a four-tab strip cost one stop instead of four.
 *
 * `idFor` is exported so the caller can point its panel back at the tab:
 * `<div role="tabpanel" id={panelId} aria-labelledby={tabId}>`.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
  className,
}: {
  tabs: readonly TabDef<T>[];
  value: T;
  onChange: (key: T) => void;
  /** Names the strip itself, e.g. "Catalog sections". */
  label: string;
  idPrefix: string;
  className?: string;
}) {
  const move = (event: React.KeyboardEvent, index: number) => {
    const last = tabs.length - 1;
    // The strip flips with the writing direction, so ArrowLeft means "towards
    // the start of the line", not "towards the previous tab".
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';

    let next: number | null = null;
    if (event.key === forward) next = index === last ? 0 : index + 1;
    else if (event.key === back) next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;

    event.preventDefault();
    onChange(tabs[next].key);
    document.getElementById(`${idPrefix}-tab-${tabs[next].key}`)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-x-5 gap-y-1 border-b', className)}
    >
      {tabs.map((tab, index) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            id={`${idPrefix}-tab-${tab.key}`}
            role="tab"
            type="button"
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              '-mb-px border-b-2 px-1 pb-2 pt-1 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'border-accent font-medium text-accent'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge !== null && (
              <span className="ms-1.5 text-xs text-ink-muted tabular-nums">{tab.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The panel half of the pair, so the two ids never have to be spelled twice.
 *
 * `hidden` is for the caller that keeps every panel mounted and shows one — the
 * arrangement the ARIA pattern describes, and the one a screen needs when a
 * panel does something while it is not being looked at. `WaiterStation` is that
 * caller: the panel it hides owns the socket subscription that keeps the count
 * on its own tab true. Callers that render only the open panel simply leave
 * this alone.
 */
export function TabPanel({
  idPrefix,
  tab,
  hidden,
  children,
  className,
}: {
  idPrefix: string;
  tab: string;
  hidden?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tab}`}
      aria-labelledby={`${idPrefix}-tab-${tab}`}
      // The attribute rather than a `hidden` utility class: it takes the panel
      // out of the accessibility tree and out of the tab order on its own, and
      // it does so without depending on a stylesheet having loaded.
      hidden={hidden}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  );
}
